import { useState, useCallback } from "react";
import { useAuth } from "../App";
import { X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const FormInput = ({ label, type, value, onChange, placeholder, required, testId, children }) => (
  <div>
    <label className="block text-sm text-[#A1A1AA] mb-2 uppercase tracking-wider">{label}</label>
    {children || (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
      />
    )}
  </div>
);

const PasswordInput = ({ value, onChange, showPassword, onToggle }) => (
  <div className="relative">
    <input
      type={showPassword ? "text" : "password"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Enter password"
      required
      data-testid="password-input"
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
    >
      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  </div>
);

const AuthModal = ({ isOpen, onClose, mode, setMode }) => {
  const { login, register } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetForm = useCallback(() => {
    setEmail("");
    setPassword("");
    setUsername("");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back!");
      } else {
        await register(email, password, username);
        toast.success("Account created successfully!");
      }
      onClose();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md bg-[#0a0a12] border border-white/5 p-8 animate-fade-in-up">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white transition-colors"
          data-testid="close-auth-modal"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="font-heading text-2xl text-white mb-6">
          {mode === "login" ? "LOGIN" : "CREATE ACCOUNT"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === "register" && (
            <FormInput
              label="Username"
              type="text"
              value={username}
              onChange={setUsername}
              placeholder="Enter username"
              required
              testId="username-input"
            />
          )}

          <FormInput
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            placeholder="Enter email"
            required
            testId="email-input"
          />

          <FormInput label="Password">
            <PasswordInput
              value={password}
              onChange={setPassword}
              showPassword={showPassword}
              onToggle={() => setShowPassword(prev => !prev)}
            />
          </FormInput>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full mt-6 disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processing...
              </span>
            ) : mode === "login" ? "Login" : "Create Account"}
          </button>
        </form>

        <div className="mt-6 text-center text-[#A1A1AA]">
          {mode === "login" ? (
            <p>
              Don't have an account?{" "}
              <button onClick={() => setMode("register")} className="text-[#4F7CFF] hover:underline" data-testid="switch-to-register">
                Sign up
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{" "}
              <button onClick={() => setMode("login")} className="text-[#4F7CFF] hover:underline" data-testid="switch-to-login">
                Login
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
